import React from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';

import { withLocalePrefix } from 'shared/utils/translation';
import AppliedFilterChip, {
  type AppliedFilterChipProps,
} from 'talent_matching/components/applied_filters/applied_filter_chip';
import BaseAppliedFiltersRow from 'talent_matching/components/shared/base_applied_filters_row';
import { MY_GREENHOUSE_DEFAULT_VALUES } from 'talent_matching/my_greenhouse/my_greenhouse_default_values';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { remoteCandidatesLabel } from './remote_candidates';

const t = withLocalePrefix('talent_matching.filters.applied');

const MyGreenhouseAppliedFilters = () => {
  const { getValues } = useFormContext();

  // Chips mirror the controls in MyGreenhouseFilterRow, which offers only the
  // filters AdeptID can honor.
  const [
    savedForLater,
    minExperience,
    maxExperience,
    workPreferenceLanguages,
    workPreferenceWorksites,
    workPreferenceLocations,
    remoteCandidates,
    workPreferenceWillingToRelocate,
  ] = getValues([
    'savedForLater',
    'minExperience',
    'maxExperience',
    'workPreferenceLanguages',
    'workPreferenceWorksites',
    'workPreferenceLocations',
    'remoteCandidates',
    'workPreferenceWillingToRelocate',
  ]);

  const filters: Omit<AppliedFilterChipProps, 'formDefaults'>[] = [];
  const defaults = MY_GREENHOUSE_DEFAULT_VALUES as Record<string, unknown>;

  if (savedForLater === 'saved') {
    filters.push({
      label: t('saved_for_later'),
      filterKeys: ['savedForLater'],
      uniqueKey: 'savedForLater',
    });
  }

  if (savedForLater === 'not_saved') {
    filters.push({
      label: t('not_saved_for_later'),
      filterKeys: ['savedForLater'],
      uniqueKey: 'notSavedForLater',
    });
  }

  if (minExperience) {
    const yearsText = t('years', { count: Number(minExperience) });
    filters.push({
      label: t('minimum_experience', { years: yearsText }),
      filterKeys: ['minExperience'],
      uniqueKey: 'minimum_experience',
    });
  }

  if (maxExperience) {
    const yearsText = t('years', { count: Number(maxExperience) });
    filters.push({
      label: t('maximum_experience', { years: yearsText }),
      filterKeys: ['maxExperience'],
      uniqueKey: 'maximum_experience',
    });
  }

  if ((workPreferenceLanguages as Item[])?.length > 0) {
    const labels = (workPreferenceLanguages as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_languages', { languages: labels }),
      filterKeys: ['workPreferenceLanguages'],
      uniqueKey: 'workPreferenceLanguages',
    });
  }

  if ((workPreferenceWorksites as Item[])?.length > 0) {
    const labels = (workPreferenceWorksites as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_worksites', { worksites: labels }),
      filterKeys: ['workPreferenceWorksites'],
      uniqueKey: 'workPreferenceWorksites',
    });
  }

  if ((workPreferenceLocations as Item[])?.length > 0) {
    const labels = (workPreferenceLocations as Item[])
      .map((i) => i.label)
      .join(', ');
    filters.push({
      label: t('work_preference_locations', { locations: labels }),
      filterKeys: ['workPreferenceLocations'],
      uniqueKey: 'workPreferenceLocations',
    });
  }

  if (remoteCandidates) {
    filters.push({
      label: t('location_match', {
        scope: remoteCandidatesLabel(remoteCandidates as string),
      }),
      filterKeys: ['remoteCandidates'],
      uniqueKey: 'remoteCandidates',
    });
  }

  if (workPreferenceWillingToRelocate) {
    filters.push({
      label: t('willing_to_relocate'),
      filterKeys: ['workPreferenceWillingToRelocate'],
      uniqueKey: 'workPreferenceWillingToRelocate',
    });
  }

  const chips = filters.map((filter) => (
    <AppliedFilterChip
      key={filter.uniqueKey}
      formDefaults={defaults}
      {...filter}
    />
  ));

  return <BaseAppliedFiltersRow chips={chips} />;
};

export default MyGreenhouseAppliedFilters;
